import { z } from 'zod';

import {
  cashExpenseSourceTypeValues,
  cashExpenseStatusValues,
  type CashExpenseDto,
  type CashExpenseSourceType,
  type CashExpenseStatus,
  type CreateCashExpenseRequest,
  type CreateCashExpenseResponse,
  type ListCashExpensesRequest,
  type ListCashExpensesResponse,
  type VoidCashExpenseRequest,
  type VoidCashExpenseResponse,
} from './cash-expenses.dto.js';
import { CashDenominationRowsSchema } from './cash-denominations.schema.js';

const timestampSchema = z.iso.datetime();

export const CashExpenseStatusSchema: z.ZodType<CashExpenseStatus> =
  z.enum(cashExpenseStatusValues);
export const CashExpenseSourceTypeSchema: z.ZodType<CashExpenseSourceType> = z.enum(
  cashExpenseSourceTypeValues,
);

export const CashExpenseDtoSchema: z.ZodType<CashExpenseDto> = z
  .object({
    id: z.uuid(),
    operationId: z.uuid().nullable(),
    requestHash: z.string().length(64).nullable(),
    replacesExpenseId: z.uuid().nullable(),
    companyId: z.uuid(),
    sourceType: CashExpenseSourceTypeSchema,
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    cashLocationId: z.uuid(),
    cashSessionId: z.uuid().nullable(),
    categoryId: z.uuid().nullable(),
    amount: z.number().int().positive(),
    description: z.string().trim().min(1).max(500),
    status: CashExpenseStatusSchema,
    createdByUserId: z.uuid(),
    voidedByUserId: z.uuid().nullable(),
    voidReason: z.string().trim().min(1).max(500).nullable(),
    movementId: z.uuid(),
    reversalMovementId: z.uuid().nullable(),
    occurredAt: timestampSchema,
    voidedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((value, context) => {
    if (value.sourceType === 'drawer') {
      addRequiredSourceScopeIssue(value.storeId, context, ['storeId']);
      addRequiredSourceScopeIssue(value.locationId, context, ['locationId']);
      addRequiredSourceScopeIssue(value.stationId, context, ['stationId']);
      addRequiredSourceScopeIssue(value.cashSessionId, context, ['cashSessionId']);
      return;
    }

    if (value.sourceType === 'vault') {
      addRequiredSourceScopeIssue(value.storeId, context, ['storeId']);
      addRequiredSourceScopeIssue(value.locationId, context, ['locationId']);
      addForbiddenSourceScopeIssue(value.stationId, context, ['stationId']);
      addForbiddenSourceScopeIssue(value.cashSessionId, context, ['cashSessionId']);
      return;
    }

    addForbiddenSourceScopeIssue(value.storeId, context, ['storeId']);
    addForbiddenSourceScopeIssue(value.locationId, context, ['locationId']);
    addForbiddenSourceScopeIssue(value.stationId, context, ['stationId']);
    addForbiddenSourceScopeIssue(value.cashSessionId, context, ['cashSessionId']);
  });

export const CreateCashExpenseRequestSchema: z.ZodType<CreateCashExpenseRequest> = z.object({
  companyId: z.uuid(),
  operationId: z.uuid(),
  sourceType: CashExpenseSourceTypeSchema.optional(),
  stationId: z.uuid().nullable().optional(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  cashLocationId: z.uuid(),
  cashSessionId: z.uuid().nullable().optional(),
  categoryId: z.uuid().nullable().optional(),
  amount: z.number().int().positive(),
  description: z.string().trim().min(1).max(500),
  occurredAt: timestampSchema.optional(),
  denominations: CashDenominationRowsSchema.nullable().optional(),
});

export const CreateCashExpenseResponseSchema: z.ZodType<CreateCashExpenseResponse> = z.object({
  cashExpense: CashExpenseDtoSchema,
  warnings: z.array(z.string().trim().min(1).max(200)).optional(),
});

export const VoidCashExpenseRequestSchema: z.ZodType<VoidCashExpenseRequest> = z.object({
  companyId: z.uuid(),
  operationId: z.uuid(),
  expenseId: z.uuid(),
  sourceType: CashExpenseSourceTypeSchema.optional(),
  stationId: z.uuid().nullable().optional(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  voidReason: z.string().trim().min(1).max(500).nullable().optional(),
  occurredAt: timestampSchema.optional(),
});

export const VoidCashExpenseResponseSchema: z.ZodType<VoidCashExpenseResponse> = z.object({
  cashExpense: CashExpenseDtoSchema,
});

export const ListCashExpensesRequestSchema: z.ZodType<ListCashExpensesRequest> = z.object({
  companyId: z.uuid(),
  sourceType: CashExpenseSourceTypeSchema.optional(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  stationId: z.uuid().optional(),
  cashLocationId: z.uuid().optional(),
  cashSessionId: z.uuid().optional(),
  categoryId: z.uuid().optional(),
  status: CashExpenseStatusSchema.optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
});

export const ListCashExpensesResponseSchema: z.ZodType<ListCashExpensesResponse> = z.object({
  items: z.array(CashExpenseDtoSchema),
});

function addRequiredSourceScopeIssue(
  value: string | null,
  context: z.RefinementCtx,
  path: (string | number)[],
): void {
  if (value) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: 'Source scope field is required for this expense source.',
    path,
  });
}

function addForbiddenSourceScopeIssue(
  value: string | null,
  context: z.RefinementCtx,
  path: (string | number)[],
): void {
  if (value === null) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: 'Source scope field is not allowed for this expense source.',
    path,
  });
}
